import React, { useRef } from "react";
import {
    Grid,
    Paper,
    Typography,
    Box,
    Divider,
    alpha,
    useTheme,
    Stack,

    Card,
    CardContent,

} from "@mui/material";
import {
    LocalShipping as CourierIcon,
    QrCode as QrCodeIcon,
    Print as PrintIcon,

    Person as PersonIcon,

    Phone as PhoneIcon,
    LocationOn as LocationIcon,
    Inventory as OrderIcon,
    Payments as PaymentIcon,
    Scale as WeightIcon,
    Save as SaveIcon
} from "@mui/icons-material";
import WSButton from "../../Components/Button/WSButton";
import WSTextField, { CurrencyType, TextType } from "../../Components/TextBox/WSTextBox";
import WSPhone from "../../Components/Phone/WSPhone";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { callAPI, Method } from "../../commonFunctions/CommonFunctions";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import config from "../../Config/clientConfig.json";
import WSSelectField from "../../Components/Select/WSSelect";

const ShippingLabel = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const printRef = useRef<HTMLDivElement>(null);
    const { showMessage } = useSnackbar();
    const params = useParams();
    const id = params.id;

    const [loading, setLoading] = React.useState(false);

    const { control, handleSubmit, setValue, reset } = useForm({
        defaultValues: {
            courier: '',
            awb: '',
            shipFrom: {
                name: '',
                address: '',
                city: '',
                state: '',
                pincode: '',
                phone: ''
            },
            shipTo: {
                name: '',
                address: '',
                city: '',
                state: '',
                pincode: '',
                phone: ''
            },
            orderId: '',
            paymentMethod: '',
            paymentAmount: 0,
            weight: 0

        },
    });

    const handleSaveAndPrint = () => {
        handleSubmit((data) => handleData(data, true))();
    };

    const handlePrint = (data: any) => {

        const printWindow = window.open("", "_blank");

        if (!printWindow) return;

        const printContent = `
<html>
<head>
<title>Shipping Label - ${data.awb}</title>

<style>

@page {
  size: 4in 6in;
  margin: 0;
}

body{
  font-family: Arial, sans-serif;
  width: 4in;
  height: 6in;
  margin:0;
  padding:10px;
  box-sizing:border-box;
}

.label{
  border:2px solid black;
  height:100%;
  display:flex;
  flex-direction:column;
}

.header{
  text-align:center;
  font-weight:bold;
  font-size:18px;
  padding:6px;
  border-bottom:2px solid black;
}

.awb{
  text-align:center;
  font-size:24px;
  font-weight:bold;
  padding:8px;
  border-bottom:2px solid black;
}

.section{
  padding:8px;
  border-bottom:1px solid #000;
  font-size:13px;
}

.section-title{
  font-weight:bold;
  margin-bottom:3px;
}

.address{
  line-height:1.3;
}

.row{
  display:flex;
  justify-content:space-between;
}

.barcode{
  text-align:center;
  padding:10px;
}

.barcode-number{
  font-size:16px;
  font-weight:bold;
  letter-spacing:2px;
}

.footer{
  text-align:center;
  font-size:12px;
  padding:6px;
}

</style>
</head>

<body>

<div class="label">

<div class="header">
${data.courierName}
</div>

<div class="awb">
AWB: ${data.awb}
</div>

<div class="section">
<div class="section-title">Ship To</div>
<div class="address">
<b>${data.shipTo.name}</b><br/>
${data.shipTo.addressLine}<br/>
${data.shipTo.city}, ${data.shipTo.state} - ${data.shipTo.pincode}<br/>
📞 ${data.shipTo.phone}
</div>
</div>

<div class="section">
<div class="section-title">Ship From</div>
<div class="address">
<b>${data.shipFrom.name}</b><br/>
${data.shipFrom.addressLine}<br/>
${data.shipFrom.city}, ${data.shipFrom.state} - ${data.shipFrom.pincode}<br/>
📞 ${data.shipFrom.phone}
</div>
</div>

<div class="section row">
<div>
<b>Order ID</b><br/>
${data.orderId}
</div>

<div>
<b>Weight</b><br/>
${data.weight} kg
</div>
</div>

<div class="section row">
<div>
<b>Payment</b><br/>
${data.paymentMode}
</div>

<div>
<b>Amount</b><br/>
₹${data.paymentAmount}
</div>
</div>

<div class="barcode">
<div style="font-size:40px; letter-spacing:6px;">
|||| ||| |||| || |||
</div>

<div class="barcode-number">
${data.barcode}
</div>
</div>

<div class="footer">
Generated Shipping Label
</div>

</div>

</body>
</html>
`;

        printWindow.document.open();
        printWindow.document.write(printContent);
        printWindow.document.close();

        printWindow.onload = () => {
            printWindow.focus();
            printWindow.print();
            printWindow.close();
        };
    };

    const handleData = async (data: any, isPrint: boolean) => {
        try {
            setLoading(true);

            const request = {
                courierName: data.courier || "",
                awb: data.awbNumber || "",

                shipFrom: {
                    name: data.shipFromName || "",
                    addressLine: data.shipFromAddress || "",
                    city: data.shipFromCity || "",
                    state: data.shipFromState || "",
                    pincode: data.shipFromPincode || "",
                    phone: data.shipFromPhone || "",
                },

                shipTo: {
                    name: data.shipToName || "",
                    addressLine: data.shipToAddress || "",
                    city: data.shipToCity || "",
                    state: data.shipToState || "",
                    pincode: data.shipToPincode || "",
                    phone: data.shipToPhone || "",
                },

                orderId: data.orderId || "",
                paymentAmount: Number(data.paymentAmount) || 0,
                paymentMode: data.paymentMethod || "COD",
                weight: Number(data.weight) || 0,

                barcode: data.awbNumber || "",
                createdAt: new Date().toISOString(),
            };

            let response;

            if (id) {
                response = await callAPI(
                    `Vyapar/CourierShippment/${id}`,
                    Method.PUT,
                    request
                );
            } else {
                response = await callAPI(
                    "Vyapar/CourierShippment",
                    Method.POST,
                    request
                );
            }


            if (response.success) {

                showMessage(response.message || "Shipping label saved successfully", "success");
                if (isPrint) {
                    handlePrint(request);
                } else {
                    navigate('/shipping/list');
                }
            }
        } catch (error: any) {
            console.error("Error saving shipping label:", error);
            showMessage(error.response?.data?.message || 'Failed to save shipping label. Please try again.', 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ p: 3, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
            <form onSubmit={handleSubmit((data) => handleData(data, false))}>
                <Grid container spacing={3}>
                    {/* Header Actions */}
                    <Grid item xs={12}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                borderRadius: 2,
                                bgcolor: alpha(theme.palette.background.paper, 0.8),
                                backdropFilter: 'blur(8px)',
                                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center'
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Box
                                    sx={{
                                        width: 48,
                                        height: 48,
                                        borderRadius: 2,
                                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <CourierIcon sx={{ color: theme.palette.primary.main, fontSize: 28 }} />
                                </Box>
                                <Box>
                                    <Typography variant="h6" fontWeight={600}>
                                        Shipping Label Generator
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Enter shipping details below
                                    </Typography>
                                </Box>
                            </Box>

                            <Stack direction="row" spacing={2}>

                                <WSButton
                                    id="cancelBtn"
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    label="Cancel"
                                    onClick={() => navigate('/shipping/list')}
                                />

                                <WSButton
                                    id="saveAndPrintBtn"
                                    variant="contained"
                                    color="secondary"
                                    size="small"
                                    startIcon={<PrintIcon />}
                                    label="Save & Print"
                                    onClick={handleSaveAndPrint}
                                />

                                <WSButton
                                    id="saveBtn"
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    label="Save"
                                    type="submit"
                                    startIcon={<SaveIcon />}
                                />
                            </Stack>
                        </Paper>
                    </Grid>

                    {/* Form Section */}
                    <Grid item xs={12}>
                        <Card
                            elevation={0}
                            sx={{
                                borderRadius: 2,
                                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                height: '100%'
                            }}
                        >
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" fontWeight={600} gutterBottom>
                                    Enter Shipping Details
                                </Typography>

                                <Stack spacing={3} sx={{ mt: 2 }}>
                                    {/* Courier Details */}
                                    <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.02), borderRadius: 2 }}>
                                        <Typography variant="subtitle2" fontWeight={600} gutterBottom color="primary">
                                            Courier Information
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <WSTextField
                                                    id="courier"
                                                    label="Courier"
                                                    control={control}
                                                    name="courier"
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <WSTextField
                                                    id="awbNumber"
                                                    label="AWB Number"
                                                    control={control}
                                                    name="awbNumber"
                                                />
                                            </Grid>
                                        </Grid>
                                    </Paper>

                                    {/* Ship From */}
                                    <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.info.main, 0.02), borderRadius: 2 }}>
                                        <Typography variant="subtitle2" fontWeight={600} gutterBottom color="info.main">
                                            SHIP FROM
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <WSTextField
                                                    id="name"
                                                    label="Name/Company"
                                                    control={control}
                                                    name="shipFromName"
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <WSTextField
                                                    id="address"
                                                    label="Address"
                                                    control={control}
                                                    name="shipFromAddress"
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <WSTextField
                                                    id="city"
                                                    label="City"
                                                    control={control}
                                                    name="shipFromCity"
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <WSTextField
                                                    id="state"
                                                    label="State"
                                                    control={control}
                                                    name="shipFromState"
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <WSTextField
                                                    id="pincode"
                                                    label="Pincode"
                                                    control={control}
                                                    name="shipFromPincode"
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <WSPhone id="shipFromPhone" control={control} label='Phone' name="shipFromPhone" />
                                            </Grid>
                                        </Grid>
                                    </Paper>

                                    {/* Ship To */}
                                    <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.success.main, 0.02), borderRadius: 2 }}>
                                        <Typography variant="subtitle2" fontWeight={600} gutterBottom color="success.main">
                                            SHIP TO
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <WSTextField
                                                    label="Name"
                                                    control={control}
                                                    name="shipToName"
                                                    id="shipToName"
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <WSTextField
                                                    label="Address"
                                                    control={control}
                                                    name="shipToAddress"
                                                    id="shipToAddress"
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <WSTextField
                                                    label="City"
                                                    control={control}
                                                    name="shipToCity"
                                                    id="shipToCity"
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <WSTextField
                                                    label="State"
                                                    control={control}
                                                    name="shipToState"
                                                    id="shipToState"
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <WSTextField
                                                    label="Pincode"
                                                    type={TextType.AllowOnlyNumber}
                                                    control={control}
                                                    name="shipToPincode"
                                                    id="shipToPincode"
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <WSPhone id="shipToPhone" control={control} label='Phone' name="shipToPhone" />
                                            </Grid>
                                        </Grid>
                                    </Paper>

                                    {/* Order Details */}
                                    <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.warning.main, 0.02), borderRadius: 2 }}>
                                        <Typography variant="subtitle2" fontWeight={600} gutterBottom color="warning.main">
                                            Order Details
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={4}>
                                                <WSTextField
                                                    label="Order ID"
                                                    control={control}
                                                    name="orderId"
                                                    id="orderId"
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <WSSelectField
                                                    label="Payment Type"
                                                    id="paymentMethod"
                                                    name="paymentType"
                                                    control={control}
                                                    options={config.paymentType.staticOptionData}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <WSTextField
                                                    label="Amount"
                                                    type={TextType.Currency}
                                                    currency={CurrencyType.Rupee}
                                                    control={control}
                                                    name="paymentAmount"
                                                    id="paymentAmount"
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <WSTextField
                                                    label="Weight (KG)"
                                                    type={TextType.AllowOnlyNumber}
                                                    control={control}
                                                    name="weight"
                                                    id="weight"
                                                />
                                            </Grid>
                                        </Grid>
                                    </Paper>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </form>

            <div ref={printRef} style={{ display: 'none' }} />
        </Box>
    );
};

export default ShippingLabel;